import { cn } from '@/lib/utils'
import { AlertCircle, CheckCircle, File, FileImage, FileText, Upload, X, Info } from 'lucide-react'
import { DragEvent, useCallback, useRef, useState } from 'react'
import api from '../../services/api'
import { useMessage } from '../../hooks/useMessage'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Progress } from '../ui/progress'
import { Switch } from '../ui/switch'
import { Label } from '../ui/label'
import { AgentProgress } from '../common/AgentProgress'

interface UploadModalProps {
  isOpen: boolean
  onClose: () => void
  conversationId?: string
  onUploadComplete?: (files: Array<{ filename: string; purpose: string }>) => void
  skipSummaryMessage?: boolean
}

interface UploadFile {
  id: string
  file: File
  progress: number
  status: 'pending' | 'uploading' | 'success' | 'error'
  error?: string
  category?: 'images' | 'documents' | 'others'
  savedName?: string
  purpose: 'READ_UNDERSTAND' | 'ADD_TO_SCENES'
}

const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB
const MAX_FILES = 10

type AnalysisPhase = 'uploading' | 'analyzing' | 'complete' | null;

export function UploadModal({ isOpen, onClose, conversationId, onUploadComplete, skipSummaryMessage = false }: UploadModalProps) {
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [defaultPurpose, setDefaultPurpose] = useState<'READ_UNDERSTAND' | 'ADD_TO_SCENES'>('READ_UNDERSTAND')
  const [analysisJobId, setAnalysisJobId] = useState<string | null>(null)
  const [analysisPhase, setAnalysisPhase] = useState<AnalysisPhase>(null)
  const [uploadedFileCount, setUploadedFileCount] = useState(0)
  const [completedUploadResults, setCompletedUploadResults] = useState<Array<{ filename: string; purpose: string }>>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { sendMessage } = useMessage()

  const getFileIcon = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase()
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext || '')) {
      return <FileImage className="h-8 w-8 text-primary" />
    }
    
    if (['pdf', 'doc', 'docx', 'txt', 'md', 'rtf', 'odt'].includes(ext || '')) {
      return <FileText className="h-8 w-8 text-green-500" />
    }
    
    return <File className="h-8 w-8 text-gray-500" />
  }

  const getFileCategory = (fileName: string): 'images' | 'documents' | 'others' => {
    const ext = fileName.split('.').pop()?.toLowerCase()
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext || '')) {
      return 'images'
    }
    
    if (['pdf', 'doc', 'docx', 'txt', 'md', 'rtf', 'odt'].includes(ext || '')) {
      return 'documents'
    }
    
    return 'others'
  }

  const sanitizeFilename = (filename: string): string => {
    const ext = filename.split('.').pop() || ''
    const baseName = filename.replace(`.${ext}`, '')
    
    // Remove special characters, keep only alphanumeric, hyphens, underscores
    const sanitized = baseName
      .replace(/[^a-zA-Z0-9\-_]/g, '_') // Replace special chars with underscore
      .replace(/_{2,}/g, '_') // Replace multiple underscores with single
      .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
    
    return sanitized + (ext ? `.${ext}` : '')
  }

  const sendUploadSummaryMessage = async (uploadResults: any[]) => {
    if (!conversationId) return

    const successfulUploads = uploadResults.filter(r => r.success)
    if (successfulUploads.length === 0) return

    // Group files by category
    const imageFiles = successfulUploads.filter(r => r.category === 'images')
    const documentFiles = successfulUploads.filter(r => r.category === 'documents')
    const otherFiles = successfulUploads.filter(r => r.category === 'others')

    // Build message content with resourceId and URL so the agent can use the files
    let messageContent = `I've uploaded ${successfulUploads.length} file${successfulUploads.length > 1 ? 's' : ''} to the project resources:\n\n`

    const formatFile = (file: any) => {
      let line = `- **${file.originalName || file.savedName}**`
      if (file.resourceId) line += ` (resourceId: \`${file.resourceId}\`)`
      if (file.baseUrl) line += `\n  URL: ${file.baseUrl}`
      else if (file.signedUrl) line += `\n  URL: ${file.signedUrl}`
      return line
    }

    if (imageFiles.length > 0) {
      messageContent += `**Images** (${imageFiles.length}):\n`
      imageFiles.forEach(file => { messageContent += formatFile(file) + '\n' })
      messageContent += '\n'
    }

    if (documentFiles.length > 0) {
      messageContent += `**Documents** (${documentFiles.length}):\n`
      documentFiles.forEach(file => { messageContent += formatFile(file) + '\n' })
      messageContent += '\n'
    }

    if (otherFiles.length > 0) {
      messageContent += `**Other files** (${otherFiles.length}):\n`
      otherFiles.forEach(file => { messageContent += formatFile(file) + '\n' })
      messageContent += '\n'
    }

    messageContent += `Please analyze these resources and use them in the video.`

    // Send message to trigger assistant response
    try {
      await sendMessage({
        conversationId,
        content: messageContent
      })
    } catch (error) {
      console.error('Failed to send upload summary message:', error)
    }
  }

  const getPreviewFilename = (file: File, category: 'images' | 'documents' | 'others', fileId: string): string => {
    const ext = file.name.split('.').pop() || ''
    
    if (category === 'images') {
      // Count how many image files come before this one in the upload list
      const currentIndex = uploadFiles.findIndex(f => f.id === fileId)
      const imagesBefore = uploadFiles.slice(0, currentIndex).filter(f => f.category === 'images').length
      return `ref_${imagesBefore + 1}.${ext}`
    } else {
      const sanitized = sanitizeFilename(file.name)
      return sanitized || `file_${Date.now()}.${ext}`
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size is ${formatFileSize(MAX_FILE_SIZE)}`
    }
    return null
  }

  const addFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files)

    // Check total files limit
    if (uploadFiles.length + fileArray.length > MAX_FILES) {
      alert(`Maximum ${MAX_FILES} files allowed`)
      return
    }

    const newFiles: UploadFile[] = fileArray.map(file => {
      const error = validateFile(file)
      return {
        id: Math.random().toString(36).substr(2, 9),
        file,
        progress: 0,
        status: error ? 'error' : 'pending',
        error: error || undefined,
        category: getFileCategory(file.name),
        purpose: defaultPurpose
      }
    })

    setUploadFiles(prev => [...prev, ...newFiles])
  }, [uploadFiles.length, defaultPurpose])

  const removeFile = (id: string) => {
    setUploadFiles(prev => prev.filter(f => f.id !== id))
  }

  const clearAll = () => {
    setUploadFiles([])
    setAnalysisJobId(null)
    setAnalysisPhase(null)
    setUploadedFileCount(0)
    setCompletedUploadResults([])
  }

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const files = e.dataTransfer.files
    if (files.length > 0) {
      addFiles(files)
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      addFiles(files)
    }
    // Reset input value to allow selecting same file again
    e.target.value = ''
  }

  const handleUploadFiles = async () => {
    if (!conversationId) {
      alert('No conversation selected')
      return
    }

    const pendingFiles = uploadFiles.filter(f => f.status === 'pending')
    if (pendingFiles.length === 0) {
      return
    }

    setIsUploading(true)

    try {
      const formData = new FormData()
      formData.append('conversationId', conversationId)

      // Collect all purposes as an array
      const purposesArray = pendingFiles.map(f => f.purpose)
      formData.append('purposes', JSON.stringify(purposesArray))

      // Append files
      pendingFiles.forEach(uploadFile => {
        formData.append('files', uploadFile.file)
      })

      // Update status to uploading
      setUploadFiles(prev => prev.map(f => 
        pendingFiles.find(pf => pf.id === f.id) 
          ? { ...f, status: 'uploading', progress: 0 }
          : f
      ))

      const response = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5 minutes timeout for file uploads (up to 100MB)
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          )

          // Update progress for all uploading files
          setUploadFiles(prev => prev.map(f =>
            f.status === 'uploading'
              ? { ...f, progress: percentCompleted }
              : f
          ))
        }
      })

      if (response.data.success) {
        // Update file statuses based on results
        const results = response.data.results

        setUploadFiles(prev => prev.map(f => {
          const result = results.find((r: any) => r.originalName === f.file.name)
          if (result) {
            return {
              ...f,
              status: result.success ? 'success' : 'error',
              progress: 100,
              error: result.error,
              savedName: result.savedName
            }
          }
          return f
        }))

        // Show success message
        const successCount = results.filter((r: any) => r.success).length
        const failCount = results.filter((r: any) => !r.success).length

        // Store analysis job ID if returned and set analysis phase
        if (response.data.analysisJobId && successCount > 0) {
          setAnalysisJobId(response.data.analysisJobId)
          setUploadedFileCount(successCount)
          setAnalysisPhase('analyzing')

          // Store upload results to call onUploadComplete after analysis
          const uploadedFiles = results
            .filter((r: any) => r.success)
            .map((r: any) => ({
              filename: r.originalName,
              purpose: r.purpose
            }))
          setCompletedUploadResults(uploadedFiles)
        }
        
        let message = `Successfully uploaded ${successCount} files`
        if (failCount > 0) {
          message += `, ${failCount} failed`
        }
        
        if (response.data.gitCommit) {
          message += ` and committed to git`
        }
        
        console.log(message)

        // Send upload summary message to trigger assistant response
        // Skip if called from landing page (before user sends first message)
        if (successCount > 0 && !skipSummaryMessage) {
          await sendUploadSummaryMessage(results)
        }

        // Don't call onUploadComplete yet if analysis is running
        // It will be called after analysis completes
        if (onUploadComplete && successCount > 0 && !response.data.analysisJobId) {
          const uploadedFiles = results
            .filter((r: any) => r.success)
            .map((r: any) => ({
              filename: r.originalName,
              purpose: r.purpose
            }))
          onUploadComplete(uploadedFiles)
        }

        // Auto-close modal if backend signals to do so AND no analysis is running
        // If analysis is running, modal will close after analysis completes
        if (response.data.autoClose && successCount > 0 && !response.data.analysisJobId) {
          setTimeout(() => {
            handleClose()
          }, 1500) // Give user time to see the success status
        }
        
      } else {
        throw new Error(response.data.error || 'Upload failed')
      }

    } catch (error) {
      console.error('Upload error:', error)
      
      // Mark all uploading files as error
      setUploadFiles(prev => prev.map(f => 
        f.status === 'uploading' 
          ? { ...f, status: 'error', error: 'Upload failed' }
          : f
      ))
      
      alert('Upload failed. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    if (!isUploading) {
      clearAll()
      onClose()
    }
  }

  const hasValidFiles = uploadFiles.some(f => f.status === 'pending')
  const hasSuccessfulUploads = uploadFiles.some(f => f.status === 'success')

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            {analysisPhase === 'analyzing' ? 'Analyzing Resources' :
             analysisPhase === 'complete' ? 'Analysis Complete' :
             'Upload Files to Resources'}
          </DialogTitle>
        </DialogHeader>

        {/* Analysis Complete View */}
        {analysisPhase === 'complete' && (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <CheckCircle className="h-16 w-16 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Analysis Complete!</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  video-context.md has been created successfully
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Analysis in Progress View */}
        {analysisPhase === 'analyzing' && (
          <div className="flex-1 flex items-center justify-center p-6">
            <AgentProgress
              jobId={analysisJobId}
              showThinking={true}
              compact={false}
              estimatedDurationSeconds={uploadedFileCount * 30}
              fileCount={uploadedFileCount}
              onComplete={(result) => {
                console.log('Resource analysis completed:', result)
                setAnalysisPhase('complete')

                // Call onUploadComplete now that analysis is done
                if (onUploadComplete && completedUploadResults.length > 0) {
                  onUploadComplete(completedUploadResults)
                }

                // Auto-close after showing completion message for 2.5 seconds
                setTimeout(() => {
                  handleClose()
                }, 2500)
              }}
              onError={(error) => {
                console.error('Resource analysis failed:', error)
                setAnalysisPhase(null)

                // Call onUploadComplete even if analysis failed - files were still uploaded
                if (onUploadComplete && completedUploadResults.length > 0) {
                  onUploadComplete(completedUploadResults)
                }

                // Close modal after error
                setTimeout(() => {
                  handleClose()
                }, 1500)
              }}
              className="w-full max-w-lg"
            />
          </div>
        )}

        {/* Upload UI (default view) */}
        {analysisPhase !== 'analyzing' && analysisPhase !== 'complete' && (
          <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Classification Toggle */}
          <div className="bg-muted/30 rounded-lg p-4 border">
            <div className="flex items-center justify-between mb-3">
              <Label htmlFor="purpose-toggle" className="text-base font-medium cursor-pointer">
                How will you use these files?
              </Label>
              <Switch
                id="purpose-toggle"
                checked={defaultPurpose === 'ADD_TO_SCENES'}
                onCheckedChange={(checked) => {
                  const newPurpose = checked ? 'ADD_TO_SCENES' : 'READ_UNDERSTAND'
                  setDefaultPurpose(newPurpose)
                  // Update all pending files with new purpose
                  setUploadFiles(prev => prev.map(f =>
                    f.status === 'pending' ? { ...f, purpose: newPurpose } : f
                  ))
                }}
                disabled={isUploading}
              />
            </div>

            <div className="space-y-2">
              <div className={cn(
                "flex items-start gap-2 p-2 rounded transition-colors",
                defaultPurpose === 'READ_UNDERSTAND' && "bg-accent border border-primary/30"
              )}>
                <Info className={cn(
                  "h-4 w-4 mt-0.5 flex-shrink-0",
                  defaultPurpose === 'READ_UNDERSTAND' ? "text-primary" : "text-muted-foreground"
                )} />
                <div>
                  <p className={cn(
                    "text-sm font-medium",
                    defaultPurpose === 'READ_UNDERSTAND' ? "text-accent-foreground" : "text-foreground"
                  )}>
                    Read & Understand
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Extract context, facts, themes, or style from the files for conversation analysis
                  </p>
                </div>
              </div>

              <div className={cn(
                "flex items-start gap-2 p-2 rounded transition-colors",
                defaultPurpose === 'ADD_TO_SCENES' && "bg-green-50 border border-green-200"
              )}>
                <Info className={cn(
                  "h-4 w-4 mt-0.5 flex-shrink-0",
                  defaultPurpose === 'ADD_TO_SCENES' ? "text-green-600" : "text-muted-foreground"
                )} />
                <div>
                  <p className={cn(
                    "text-sm font-medium",
                    defaultPurpose === 'ADD_TO_SCENES' ? "text-green-900" : "text-foreground"
                  )}>
                    Add to Scenes
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Use these assets directly in video slides (uploaded to cloud storage)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
              isDragOver 
                ? "border-primary bg-primary/5" 
                : "border-border hover:border-primary/50"
            )}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className={cn(
              "h-12 w-12 mx-auto mb-4",
              isDragOver ? "text-primary" : "text-muted-foreground"
            )} />
            <p className="text-lg font-medium mb-2">
              {isDragOver ? "Drop files here" : "Drag & drop files here"}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              or click to browse files
            </p>
            <p className="text-xs text-muted-foreground">
              Maximum {MAX_FILES} files, {formatFileSize(MAX_FILE_SIZE)} per file
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Images → resources/images (renamed as ref_1.ext, ref_2.ext, etc.)
            </p>
            <p className="text-xs text-muted-foreground">
              Documents/Others → resources/documents|others (special characters removed)
            </p>
          </div>

          {/* File List */}
          {uploadFiles.length > 0 && (
            <div className="flex-1 overflow-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">Files ({uploadFiles.length})</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  disabled={isUploading}
                >
                  Clear All
                </Button>
              </div>
              
              <div className="space-y-2">
                {uploadFiles.map((uploadFile) => (
                  <div
                    key={uploadFile.id}
                    className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex-shrink-0">
                      {getFileIcon(uploadFile.file)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {uploadFile.file.name}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            Will be saved as: {getPreviewFilename(uploadFile.file, uploadFile.category || 'others', uploadFile.id)}
                          </p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <span className={cn(
                            "text-xs px-2 py-0.5 rounded-full",
                            uploadFile.category === 'images' && "bg-accent text-accent-foreground",
                            uploadFile.category === 'documents' && "bg-green-100 text-green-700",
                            uploadFile.category === 'others' && "bg-gray-100 text-gray-700"
                          )}>
                            {uploadFile.category}
                          </span>
                          <span className={cn(
                            "text-xs px-2 py-0.5 rounded-full",
                            uploadFile.purpose === 'READ_UNDERSTAND' && "bg-accent text-accent-foreground",
                            uploadFile.purpose === 'ADD_TO_SCENES' && "bg-green-100 text-green-700"
                          )}>
                            {uploadFile.purpose === 'READ_UNDERSTAND' ? 'Understand' : 'Scenes'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(uploadFile.file.size)}
                        </p>
                        
                        {uploadFile.status === 'uploading' && (
                          <div className="flex-1">
                            <Progress value={uploadFile.progress} className="h-1" />
                          </div>
                        )}
                        
                        {uploadFile.status === 'success' && (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="h-4 w-4" />
                            <span className="text-xs">Uploaded</span>
                          </div>
                        )}
                        
                        {uploadFile.status === 'error' && (
                          <div className="flex items-center gap-1 text-red-600">
                            <AlertCircle className="h-4 w-4" />
                            <span className="text-xs">{uploadFile.error}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(uploadFile.id)}
                      disabled={isUploading}
                      className="flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        )}

        {/* Actions - Only show for upload phase */}
        {analysisPhase !== 'analyzing' && analysisPhase !== 'complete' && (
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {hasSuccessfulUploads && (
                <span className="text-green-600">
                  {uploadFiles.filter(f => f.status === 'success').length} files uploaded successfully
                </span>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isUploading}
              >
                {hasSuccessfulUploads ? 'Done' : 'Cancel'}
              </Button>

              {hasValidFiles && (
                <Button
                  onClick={handleUploadFiles}
                  disabled={isUploading || !conversationId}
                  className="min-w-24"
                >
                  {isUploading ? 'Uploading...' : 'Upload Files'}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileInputChange}
        />
      </DialogContent>
    </Dialog>
  )
} 